// Promotion Swiper Centralized Initializer
(function () {
  if (window.__promotionImageComponentLoaded) return;
  window.__promotionImageComponentLoaded = true;

  const promotionSwiperInstances = new WeakMap();

  function initPromotionSwiper(section) {
    if (!(section instanceof HTMLElement)) return;
    if (promotionSwiperInstances.has(section)) return;

    const swiperElement = section.querySelector(
      '.promotion-image-section__viewport.swiper'
    );

    if (!swiperElement) return;

    if (swiperElement.swiper) {
      promotionSwiperInstances.set(section, swiperElement.swiper);
      section.dataset.promotionSwiperInitialized = 'true';
      return;
    }

    const slides = swiperElement.querySelectorAll('.swiper-slide');

    if (slides.length <= 1) {
      section.dataset.promotionSwiperInitialized = 'true';
      return;
    }

    if (
      !window.FoxTheme ||
      typeof window.FoxTheme.Carousel !== 'function' ||
      !window.FoxTheme.Swiper
    ) {
      const retryCount = Number(
        section.dataset.promotionSwiperRetry || 0
      );

      if (retryCount >= 100) {
        console.warn(
          '[Promotion Slider] FoxTheme Carousel was not available.'
        );
        return;
      }

      section.dataset.promotionSwiperRetry = String(
        retryCount + 1
      );

      window.setTimeout(() => {
        initPromotionSwiper(section);
      }, 50);

      return;
    }

    const pagination = section.querySelector(
      '.promotion-image-section__dots.swiper-pagination'
    );

    function syncPromotionPagination(swiper) {
      if (!pagination || !swiper) return;

      const activeIndex = Number.isFinite(swiper.realIndex)
        ? swiper.realIndex
        : swiper.activeIndex;

      pagination
        .querySelectorAll('.promotion-image-section__dot')
        .forEach((bullet, index) => {
          const isActive = index === activeIndex;

          bullet.classList.toggle('is-active', isActive);
          bullet.classList.toggle(
            'swiper-pagination-bullet-active',
            isActive
          );
          bullet.setAttribute(
            'aria-current',
            isActive ? 'true' : 'false'
          );
        });
    }

    const sliderOptions = {
      slidesPerView: 1,
      spaceBetween: 0,
      speed: 350,
      loop: false,
      grabCursor: true,
      simulateTouch: true,
      allowTouchMove: true,
      threshold: 2,
      shortSwipes: true,
      longSwipes: true,
      longSwipesRatio: 0.08,
      longSwipesMs: 300,
      touchRatio: 1,
      resistance: true,
      resistanceRatio: 0.65,
      watchOverflow: true,
      observer: true,
      observeParents: true,
      autoHeight: false,
      pagination: pagination
        ? {
            el: pagination,
            clickable: true,
            renderBullet(index, className) {
              return `<span class="${className} promotion-image-section__dot" data-promotion-bullet-index="${index}" role="button" tabindex="0" aria-label="Go to slide ${index + 1}"></span>`;
            }
          }
        : undefined,
      on: {
        init(swiper) {
          window.requestAnimationFrame(() => {
            syncPromotionPagination(swiper);
          });
        },
        slideChange(swiper) {
          syncPromotionPagination(swiper);
        },
        activeIndexChange(swiper) {
          syncPromotionPagination(swiper);
        },
        transitionEnd(swiper) {
          syncPromotionPagination(swiper);
        },
        paginationUpdate(swiper) {
          syncPromotionPagination(swiper);
        }
      }
    };

    const carouselInstance = new window.FoxTheme.Carousel(
      swiperElement,
      sliderOptions
    );
    carouselInstance.init();

    const instance = carouselInstance.slider;

    syncPromotionPagination(instance);

    if (pagination) {
      pagination.addEventListener('keydown', (event) => {
        const bullet = event.target.closest(
          '[data-promotion-bullet-index]'
        );

        if (!bullet) return;
        if (event.key !== 'Enter' && event.key !== ' ') return;

        event.preventDefault();

        const targetIndex = Number(
          bullet.dataset.promotionBulletIndex
        );

        if (Number.isFinite(targetIndex)) {
          instance.slideTo(targetIndex);
        }
      });
    }

    promotionSwiperInstances.set(section, instance);
    section.dataset.promotionSwiperInitialized = 'true';
    delete section.dataset.promotionSwiperRetry;
  }

  function destroyPromotionSwiper(section) {
    if (!(section instanceof HTMLElement)) return;

    const instance = promotionSwiperInstances.get(section);

    if (
      instance &&
      typeof instance.destroy === 'function' &&
      !instance.destroyed
    ) {
      instance.destroy(true, true);
    }

    promotionSwiperInstances.delete(section);
    delete section.dataset.promotionSwiperInitialized;
    delete section.dataset.promotionSwiperRetry;
  }

  function initPromotionVariantPicker(section) {
    if (!(section instanceof HTMLElement)) return;

    const variantSelects = section.querySelector('variant-selects');
    if (!variantSelects) return;
    if (variantSelects.dataset.promotionPickerInitialized === 'true') return;
    variantSelects.dataset.promotionPickerInitialized = 'true';

    const variantsScript = variantSelects.querySelector('[data-product-variants]');
    if (!variantsScript || !variantsScript.textContent) return;

    let allVariants = [];
    try {
      allVariants = JSON.parse(variantsScript.textContent);
    } catch (e) {
      console.warn('[Promotion Variant] Failed to parse variants JSON', e);
      return;
    }

    if (!Array.isArray(allVariants) || allVariants.length === 0) return;

    const wrapper = section.querySelector('[id^="promotion-discount-wrapper-"]');
    if (!wrapper) return;

    function getSelectedOptions() {
      const groups = variantSelects.querySelectorAll('fieldset, div.product-form__input');
      let selectedOptions = [];

      if (groups.length) {
        groups.forEach((group) => {
          const checked = group.querySelector('input[type="radio"]:checked');
          if (checked) {
            selectedOptions.push(checked.value.trim());
            return;
          }
          const select = group.querySelector('select');
          if (select && select.value) {
            selectedOptions.push(select.value.trim());
          }
        });
      }

      if (!selectedOptions.length) {
        variantSelects.querySelectorAll('input[type="radio"]:checked').forEach((r) => {
          selectedOptions.push(r.value.trim());
        });
        if (!selectedOptions.length) {
          variantSelects.querySelectorAll('select').forEach((sel) => {
            if (sel.value) selectedOptions.push(sel.value.trim());
          });
        }
      }

      return selectedOptions;
    }

    function findMatchingVariant(selectedOptions, targetVariantId) {
      if (selectedOptions.length > 0) {
        const matched = allVariants.find((v) => {
          if (Array.isArray(v.options) && v.options.length === selectedOptions.length) {
            return v.options.every(
              (opt, idx) => opt && opt.trim().toLowerCase() === selectedOptions[idx].toLowerCase()
            );
          }
          const vOpts = [v.option1, v.option2, v.option3].filter(Boolean);
          if (vOpts.length === selectedOptions.length) {
            return vOpts.every(
              (opt, idx) => opt && opt.trim().toLowerCase() === selectedOptions[idx].toLowerCase()
            );
          }
          return false;
        });
        if (matched) return matched;
      }

      if (targetVariantId) {
        const byId = allVariants.find((v) => String(v.id) === String(targetVariantId));
        if (byId) return byId;
      }

      return null;
    }

    function updateOptionStatuses() {
      const groups = Array.from(variantSelects.querySelectorAll('fieldset, div.product-form__input'));
      if (!groups.length) return;

      const currentSelections = groups.map((g) => {
        const checked = g.querySelector('input[type="radio"]:checked');
        if (checked) return checked.value.trim();
        const sel = g.querySelector('select');
        return sel && sel.value ? sel.value.trim() : '';
      });

      groups.forEach((group, groupIndex) => {
        const inputs = group.querySelectorAll('input[type="radio"], option');
        inputs.forEach((input) => {
          const val = input.value.trim();
          const testOptions = [...currentSelections];
          testOptions[groupIndex] = val;

          const matchingVariants = allVariants.filter((v) => {
            const vOpts = Array.isArray(v.options) ? v.options : [v.option1, v.option2, v.option3].filter(Boolean);
            return testOptions.every((optVal, idx) => {
              if (idx === groupIndex) return optVal && vOpts[idx] && vOpts[idx].toLowerCase() === optVal.toLowerCase();
              if (!optVal) return true;
              return vOpts[idx] && vOpts[idx].toLowerCase() === optVal.toLowerCase();
            });
          });

          const isAvailable = matchingVariants.some((v) => v.available);
          const exists = matchingVariants.length > 0;

          if (input.tagName === 'OPTION') {
            input.disabled = !exists;
            input.classList.toggle('disabled', !isAvailable);
          } else {
            input.classList.toggle('disabled', !isAvailable || !exists);
            if (input.id) {
              const labelFor = group.querySelector(`label[for="${CSS.escape(input.id)}"]`);
              if (labelFor) labelFor.classList.toggle('disabled', !isAvailable || !exists);
            }
            const label = input.nextElementSibling;
            if (label && label.tagName === 'LABEL') {
              label.classList.toggle('disabled', !isAvailable || !exists);
            }
          }
        });
      });
    }

    function applyVariant(variant, isInitial = false) {
      const checkbox = wrapper.querySelector('.promotion-row2-checkbox-input');
      const isGiftChecked = Boolean(checkbox && checkbox.checked);
      const giftPriceCents = Number(wrapper.dataset.giftPriceCents || 0);
      const giftCompareCents = Number(wrapper.dataset.giftCompareCents || 0);

      if (!variant) {
        const forms = wrapper.querySelectorAll('form[is="product-form"], form[action*="/cart/add"]');
        forms.forEach((form) => {
          const submitBtn = form.querySelector('[name="add"]') || form.querySelector('button[type="submit"]');
          if (submitBtn) {
            submitBtn.disabled = true;
            submitBtn.setAttribute('disabled', 'disabled');
            const btnText = submitBtn.querySelector('.btn__text');
            if (btnText) {
              btnText.textContent = (window.FoxTheme && window.FoxTheme.variantStrings && window.FoxTheme.variantStrings.unavailable) || 'Unavailable';
            }
          }
        });
        return;
      }

      const variantPrice = Number(variant.price || 0);
      let variantCompare = Number(variant.compare_at_price || 0);
      if (variantCompare <= variantPrice) {
        variantCompare = 0;
      }

      const uncheckedPrice = variantPrice;
      const checkedPrice = variantPrice + giftPriceCents;
      const uncheckedCompare = variantCompare;
      const checkedCompare = (variantCompare > 0 ? variantCompare : variantPrice) + giftCompareCents;

      // 1. Update Price Elements
      wrapper.querySelectorAll('[data-promotion-price-container]').forEach((container) => {
        container.dataset.promotionVariantId = String(variant.id);
        const priceEl = container.querySelector('.promotion-product-price');
        const compareEl = container.querySelector('.promotion-product-compare-price');
        if (!priceEl || !compareEl) return;

        priceEl.dataset.baseCents = String(uncheckedPrice);
        priceEl.dataset.uncheckedCents = String(uncheckedPrice);
        priceEl.dataset.checkedCents = String(checkedPrice);

        compareEl.dataset.baseCents = String(uncheckedCompare);
        compareEl.dataset.uncheckedCents = String(uncheckedCompare);
        compareEl.dataset.checkedCents = String(checkedCompare);

        const currentFinal = isGiftChecked ? checkedPrice : uncheckedPrice;
        const currentOriginal = isGiftChecked ? checkedCompare : uncheckedCompare;
        const hasDiscount = currentOriginal > currentFinal;

        priceEl.textContent = formatPromotionMoney(currentFinal);
        priceEl.classList.toggle('price__regular', hasDiscount);

        compareEl.textContent = formatPromotionMoney(currentOriginal);
        compareEl.hidden = !hasDiscount;
        container.classList.toggle('price--on-sale', hasDiscount);
      });

      // 2. Update Dynamic Badges (Corner badge on media and inline price discount badge)
      const uncheckedPercent = uncheckedCompare > uncheckedPrice
        ? Math.round(((uncheckedCompare - uncheckedPrice) * 100) / uncheckedCompare)
        : 0;
      const checkedPercent = checkedCompare > checkedPrice
        ? Math.round(((checkedCompare - checkedPrice) * 100) / checkedCompare)
        : 0;

      wrapper.querySelectorAll('.promotion-discount-badge-dynamic').forEach((badge) => {
        const fixedPercentAttr = badge.getAttribute('data-fixed-discount-percent');
        const hasFixedPercent = fixedPercentAttr !== null && fixedPercentAttr !== '' && fixedPercentAttr !== '0';

        if (!hasFixedPercent) {
          badge.dataset.discountUnchecked = String(uncheckedPercent);
          badge.dataset.discountChecked = String(checkedPercent);
        }

        const activePercent = hasFixedPercent
          ? Number(fixedPercentAttr)
          : (isGiftChecked ? checkedPercent : uncheckedPercent);

        const percentEl = badge.querySelector('.promotion-discount-percent');
        if (percentEl && Number.isFinite(activePercent)) {
          percentEl.textContent = `${activePercent}%`;
        }

        const topCopy = badge.parentElement?.querySelector('.promotion-discount-badge-top-copy');

        if (activePercent > 0) {
          badge.style.display = '';
          badge.hidden = false;
          if (topCopy) topCopy.style.display = '';
        } else {
          badge.style.display = 'none';
          if (topCopy) topCopy.style.display = 'none';
        }
      });

      // 3. Update Add To Cart (Form & Buttons)
      const forms = wrapper.querySelectorAll('form[is="product-form"], form[action*="/cart/add"]');
      forms.forEach((form) => {
        const idInputs = form.querySelectorAll('input[name="id"], .product-variant-id');
        idInputs.forEach((idInput) => {
          idInput.value = String(variant.id);
          if (variant.available) {
            idInput.disabled = false;
            idInput.removeAttribute('disabled');
          } else {
            idInput.disabled = true;
            idInput.setAttribute('disabled', 'disabled');
          }
          idInput.dispatchEvent(new Event('change', { bubbles: true }));
        });

        const submitBtn = form.querySelector('[name="add"]') || form.querySelector('button[type="submit"]');
        if (submitBtn) {
          const btnText = submitBtn.querySelector('.btn__text');
          const formContainer = form.closest('.product-form') || form;
          const isWaitlistConfigured =
            formContainer?.getAttribute('data-enable-waitlist') === 'true' ||
            submitBtn.getAttribute('data-waitlist-configured') === 'true';

          if (variant.available) {
            submitBtn.disabled = false;
            submitBtn.removeAttribute('disabled');
            submitBtn.setAttribute('type', 'submit');
            submitBtn.classList.remove('btn--waitlist');
            submitBtn.removeAttribute('data-waitlist-trigger');
            submitBtn.removeAttribute('data-variant-id');
            submitBtn.removeAttribute('data-variant-title');
            if (btnText) {
              btnText.textContent = (window.FoxTheme && window.FoxTheme.variantStrings && window.FoxTheme.variantStrings.addToCart) || 'Add to cart';
            }
          } else {
            if (isWaitlistConfigured) {
              submitBtn.disabled = false;
              submitBtn.removeAttribute('disabled');
              submitBtn.setAttribute('type', 'button');
              submitBtn.classList.add('btn--waitlist');
              submitBtn.setAttribute('data-waitlist-trigger', 'true');
              submitBtn.setAttribute('data-variant-id', String(variant.id));
              submitBtn.setAttribute('data-variant-title', variant.title || '');
              const waitlistText = submitBtn.getAttribute('data-default-waitlist-text') ||
                (window.FoxTheme && window.FoxTheme.variantStrings && window.FoxTheme.variantStrings.joinWaitlist) ||
                'JOIN THE WAITLIST';
              if (btnText) {
                btnText.textContent = waitlistText;
              }
            } else {
              submitBtn.disabled = true;
              submitBtn.setAttribute('disabled', 'disabled');
              submitBtn.setAttribute('type', 'submit');
              submitBtn.classList.remove('btn--waitlist');
              submitBtn.removeAttribute('data-waitlist-trigger');
              if (btnText) {
                btnText.textContent = (window.FoxTheme && window.FoxTheme.variantStrings && window.FoxTheme.variantStrings.soldOut) || 'Sold out';
              }
            }
          }
        }
      });

      // 4. Update Secondary Button Link (if points to product)
      const secondaryBtn = wrapper.querySelector('.promotion-product-buttons a.btn');
      if (secondaryBtn && secondaryBtn.href) {
        try {
          const u = new URL(secondaryBtn.href, window.location.origin);
          if (u.pathname.includes('/products/')) {
            u.searchParams.set('variant', String(variant.id));
            secondaryBtn.href = u.toString();
          }
        } catch (e) {}
      }

      // 5. Swiper Slide to Variant Featured Image (if matching slide exists)
      if (!isInitial && variant.featured_image && variant.featured_image.src) {
        const slides = section.querySelectorAll('.promotion-slider__slide');
        const featImgFilename = variant.featured_image.src.split('?')[0].split('/').pop();
        if (featImgFilename) {
          for (let i = 0; i < slides.length; i++) {
            const slideImg = slides[i].querySelector('img');
            if (slideImg && slideImg.src && slideImg.src.includes(featImgFilename)) {
              const instance = promotionSwiperInstances.get(section) || section.querySelector('.swiper')?.swiper;
              if (instance && typeof instance.slideTo === 'function') {
                instance.slideTo(i);
              }
              break;
            }
          }
        }
      }

      // 6. Dispatch events
      document.dispatchEvent(
        new CustomEvent('variant:changed', {
          detail: { variant },
        })
      );
      if (window.FoxTheme && window.FoxTheme.pubsub && window.FoxTheme.pubsub.PUB_SUB_EVENTS) {
        window.FoxTheme.pubsub.publish(window.FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
          data: {
            sectionId: section.dataset.id || section.id,
            variant,
          },
        });
      }

      // 7. Update other option disabled / sold out classes
      updateOptionStatuses();
    }

    function handleSelectionChange(target) {
      const selectedOptions = getSelectedOptions();
      const targetVariantId = target?.dataset?.variantId || null;
      const matched = findMatchingVariant(selectedOptions, targetVariantId);
      applyVariant(matched);
    }

    variantSelects.addEventListener('change', (event) => {
      window.requestAnimationFrame(() => {
        handleSelectionChange(event.target);
      });
    });

    if (window.FoxTheme && window.FoxTheme.pubsub && window.FoxTheme.pubsub.PUB_SUB_EVENTS) {
      window.FoxTheme.pubsub.subscribe(
        window.FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
        ({ data }) => {
          if (!data || !data.target) return;
          if (!variantSelects.contains(data.target)) return;
          window.requestAnimationFrame(() => {
            handleSelectionChange(data.target);
          });
        }
      );
    }

    // Run initial status and sync
    const initialOptions = getSelectedOptions();
    const initialVariant = findMatchingVariant(initialOptions);
    if (initialVariant) {
      applyVariant(initialVariant, true);
    } else {
      updateOptionStatuses();
    }
  }

  function initPromotionSections(root = document) {
    const sections = [];
    if (root instanceof HTMLElement && root.matches('[data-promotion-section]')) {
      sections.push(root);
    }
    if (root && root.querySelectorAll) {
      root.querySelectorAll('[data-promotion-section]').forEach((s) => sections.push(s));
    }
    sections.forEach((section) => {
      initPromotionSwiper(section);
      initPromotionVariantPicker(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initPromotionSections();
      },
      { once: true }
    );
  } else {
    initPromotionSections();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initPromotionSections(event.target);
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      event.target
        .querySelectorAll('[data-promotion-section]')
        .forEach((section) => {
          destroyPromotionSwiper(section);
        });
    }
  );

  document.addEventListener(
    'shopify:block:select',
    (event) => {
      const section = event.target.closest(
        '[data-promotion-section]'
      );

      if (!section) return;

      const instance = promotionSwiperInstances.get(section);
      const slide = event.target.closest(
        '.promotion-slider__slide'
      );

      if (!instance || !slide) return;

      const slideIndex = Number(slide.dataset.index);

      if (Number.isFinite(slideIndex)) {
        instance.slideTo(slideIndex);
      }
    }
  );

  function getPromotionLocale() {
    const htmlLocale =
      document.documentElement.getAttribute('lang');

    const shopifyLocale =
      window.Shopify &&
      window.Shopify.locale;

    return String(
      htmlLocale ||
      shopifyLocale ||
      'en-US'
    ).replace('_', '-');
  }

  function getPromotionCurrency() {
    return (
      window.Shopify &&
      window.Shopify.currency &&
      window.Shopify.currency.active
    ) || 'USD';
  }

  function formatPromotionMoney(cents) {
    let value = Number(cents);

    if (!Number.isFinite(value)) {
      value = 0;
    }

    if (
      window.FoxTheme &&
      window.FoxTheme.Currency &&
      typeof window.FoxTheme.Currency.formatMoney === 'function' &&
      window.FoxTheme.settings &&
      window.FoxTheme.settings.moneyFormat
    ) {
      return window.FoxTheme.Currency.formatMoney(
        value,
        window.FoxTheme.settings.moneyFormat
      );
    }

    const locale = getPromotionLocale();
    const currency = getPromotionCurrency();
    const amount = value / 100;

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        currencyDisplay: 'symbol'
      }).format(amount);
    } catch (error) {
      console.warn(
        '[Promotion Price] Intl formatting failed:',
        {
          locale,
          currency,
          cents: value
        },
        error
      );

      if (
        window.Shopify &&
        typeof window.Shopify.formatMoney === 'function'
      ) {
        const moneyFormat =
          window.Shopify.money_format ||
          '${{amount}}';

        return window.Shopify.formatMoney(
          value,
          moneyFormat
        );
      }

      return `${amount.toFixed(2)} ${currency}`;
    }
  }

  function getPromotionCartUrl() {
    const root =
      window.Shopify &&
      window.Shopify.routes &&
      window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';

    return `${root}cart.js`;
  }



  function getPromotionWrapper(input) {
    if (!input) return null;

    const wrapperId = input.dataset.wrapperId;

    if (wrapperId) {
      return document.getElementById(wrapperId);
    }

    return input.closest(
      '[id^="promotion-discount-wrapper-"]'
    );
  }

  function updateDynamicBadge(badge, checked) {
    if (!badge) return;

    const fixedPercentAttr = badge.getAttribute('data-fixed-discount-percent');
    const hasFixed = fixedPercentAttr !== null && fixedPercentAttr !== '' && fixedPercentAttr !== '0';

    const percent = hasFixed
      ? fixedPercentAttr
      : (checked ? badge.dataset.discountChecked : badge.dataset.discountUnchecked);

    const percentElement = badge.querySelector(
      '.promotion-discount-percent'
    );

    if (percentElement && percent != null) {
      percentElement.textContent = `${percent}%`;
    }

    const topCopy = badge.parentElement?.querySelector('.promotion-discount-badge-top-copy');

    if (Number(percent) > 0) {
      badge.style.display = '';
      badge.hidden = false;
      if (topCopy) topCopy.style.display = '';
    } else {
      badge.style.display = 'none';
      if (topCopy) topCopy.style.display = 'none';
    }
  }

  window.promotionUpdateDiscount = function (input) {
    if (!input) return;

    const wrapper = getPromotionWrapper(input);

    if (!wrapper) {
      console.warn(
        '[Promotion Discount] Wrapper not found:',
        input.dataset.wrapperId
      );
      return;
    }

    const checked = input.checked;

    wrapper
      .querySelectorAll('[data-promotion-price-container]')
      .forEach((container) => {
        const priceElement = container.querySelector(
          '.promotion-product-price'
        );

        const compareElement = container.querySelector(
          '.promotion-product-compare-price'
        );

        if (!priceElement || !compareElement) return;

        const finalCents = Number(
          checked
            ? priceElement.dataset.checkedCents
            : priceElement.dataset.uncheckedCents
        );

        const originalCents = Number(
          checked
            ? compareElement.dataset.checkedCents
            : compareElement.dataset.uncheckedCents
        );

        const hasDiscount =
          originalCents > finalCents;

        priceElement.textContent =
          formatPromotionMoney(finalCents);

        priceElement.classList.toggle(
          'price__regular',
          hasDiscount
        );

        compareElement.textContent =
          formatPromotionMoney(originalCents);

        compareElement.hidden = !hasDiscount;

        container.classList.toggle(
          'price--on-sale',
          hasDiscount
        );
      });

    const activePriceContainer = wrapper.querySelector(
      '[data-promotion-price-container]'
    );

    if (activePriceContainer) {
      const activePrice = activePriceContainer.querySelector(
        '.promotion-product-price'
      );

      const activeCompare = activePriceContainer.querySelector(
        '.promotion-product-compare-price'
      );

      if (activePrice && activeCompare) {
        const finalCents = Number(
          checked
            ? activePrice.dataset.checkedCents
            : activePrice.dataset.uncheckedCents
        );

        const originalCents = Number(
          checked
            ? activeCompare.dataset.checkedCents
            : activeCompare.dataset.uncheckedCents
        );

        const percent =
          originalCents > finalCents
            ? Math.round(
                ((originalCents - finalCents) * 100) /
                originalCents
              )
            : 0;

        wrapper
          .querySelectorAll(
            '.promotion-discount-badge-dynamic'
          )
          .forEach((badge) => {
            const fixedPercentAttr = badge.getAttribute('data-fixed-discount-percent');
            const hasFixed = fixedPercentAttr !== null && fixedPercentAttr !== '' && fixedPercentAttr !== '0';

            if (!hasFixed) {
              if (checked) {
                badge.dataset.discountChecked =
                  String(percent);
              } else {
                badge.dataset.discountUnchecked =
                  String(percent);
              }
            }

            updateDynamicBadge(badge, checked);
          });
      }
    }
  };

  function getPromotionCartAddUrl() {
    if (
      window.routes &&
      window.routes.cart_add_url
    ) {
      return window.routes.cart_add_url;
    }

    const root =
      window.Shopify &&
      window.Shopify.routes &&
      window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';

    return `${root}cart/add.js`;
  }

  async function addPromotionProducts(
    mainVariantId,
    giftVariantId,
    quantity = 1
  ) {
    const normalizedMainVariantId = String(
      mainVariantId || ''
    ).trim();
    const normalizedGiftVariantId = String(
      giftVariantId || ''
    ).trim();
    const normalizedQuantity = Math.max(
      Number.parseInt(quantity, 10) || 1,
      1
    );

    if (!/^\d+$/.test(normalizedMainVariantId)) {
      throw new Error(
        `Invalid main product variant ID: ${normalizedMainVariantId || 'empty'}`
      );
    }

    if (!/^\d+$/.test(normalizedGiftVariantId)) {
      throw new Error(
        `Invalid add-on variant ID: ${normalizedGiftVariantId || 'empty'}`
      );
    }

    const response = await fetch(
      getPromotionCartAddUrl(),
      {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: [
            {
              id: normalizedMainVariantId,
              quantity: normalizedQuantity
            },
            {
              id: normalizedGiftVariantId,
              quantity: 1
            }
          ]
        })
      }
    );

    const responseText = await response.text();
    let result = {};

    if (responseText) {
      try {
        result = JSON.parse(responseText);
      } catch (error) {
        result = { raw: responseText };
      }
    }

    if (!response.ok) {
      const errorMessage =
        result.description ||
        result.message ||
        result.error ||
        (response.status === 429
          ? 'The cart request was temporarily rate limited. Please wait a moment and try again.'
          : '') ||
        `The selected products could not be added. HTTP ${response.status}`;

      console.error(
        '[Promotion Add To Cart] Batch request failed:',
        {
          status: response.status,
          statusText: response.statusText,
          mainVariantId: normalizedMainVariantId,
          giftVariantId: normalizedGiftVariantId,
          response: result
        }
      );

      throw new Error(errorMessage);
    }

    return result;
  }

  /*
   * 勾选附加商品后：
   * 先添加附加商品，再恢复主题原有的主商品提交逻辑。
   *
   * 使用捕获阶段拦截，避免 product-form 先提交主商品。
   */
  document.addEventListener(
    'submit',
    async (event) => {
      const form = event.target;

      if (!(form instanceof HTMLFormElement)) return;

      const wrapper = form.closest(
        '[id^="promotion-discount-wrapper-"]'
      );

      if (!wrapper) return;


      const checkbox = wrapper.querySelector(
        '.promotion-row2-checkbox-input'
      );

      const giftVariantId =
        wrapper.dataset.giftVariantId;

      if (
        !checkbox ||
        !checkbox.checked ||
        !giftVariantId
      ) {
        return;
      }

      /*
       * 获取主商品信息与数量，并防止主商品与赠品相同。
       */
      const mainVariantInput = form.querySelector(
        '[name="id"]'
      );
      const quantityInput = form.querySelector(
        '[name="quantity"]'
      );
      const mainVariantId = mainVariantInput
        ? String(mainVariantInput.value || '').trim()
        : '';
      const mainQuantity = quantityInput
        ? quantityInput.value
        : 1;

      if (!mainVariantId) {
        console.warn(
          '[Promotion Add To Cart] Main variant ID was not found.'
        );
        return;
      }

      if (mainVariantId === String(giftVariantId)) {
        console.warn(
          '[Promotion Add To Cart] Gift variant is the same as the main variant.'
        );
        return;
      }

      if (form.dataset.promotionGiftLoading === 'true') {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      form.dataset.promotionGiftLoading = 'true';

      const submitter = event.submitter;
      const submitButton =
        submitter ||
        form.querySelector(
          'button[type="submit"], input[type="submit"]'
        );

      const wasDisabled = submitButton
        ? submitButton.disabled
        : false;

      if (submitButton) {
        submitButton.disabled = true;
        submitButton.setAttribute('aria-busy', 'true');
      }

      try {
        const result = await addPromotionProducts(
          mainVariantId,
          giftVariantId,
          mainQuantity
        );

        window.promotionUpdateDiscount(checkbox);

        delete form.dataset.promotionGiftLoading;

        if (submitButton) {
          submitButton.disabled = wasDisabled;
          submitButton.removeAttribute('aria-busy');
        }

        document.dispatchEvent(
          new CustomEvent('product-ajax:added', {
            detail: {
              product: result
            }
          })
        );

        document.dispatchEvent(
          new CustomEvent('cart:refresh', {
            bubbles: true,
            detail: {
              open: true
            }
          })
        );
      } catch (error) {
        delete form.dataset.promotionGiftLoading;

        if (submitButton) {
          submitButton.disabled = wasDisabled;
          submitButton.removeAttribute('aria-busy');
        }

        console.error(
          '[Promotion Add To Cart] Failed to add selected product:',
          error
        );

        const message =
          error && error.message
            ? error.message
            : 'The selected add-on could not be added. Please try again.';

        window.alert(message);
      }
    },
    true
  );

  // Removed initializePromotionPrices and related initialisation

  document.addEventListener('shopify:section:load', (event) => {
    event.target
      .querySelectorAll('.promotion-row2-checkbox-input')
      .forEach((input) => {
        window.promotionUpdateDiscount(input);
      });
  });
})();
